// src/api/boatApi.ts
import axios from "axios";
import { message } from "antd"; // Import Ant Design message component
import { SearchOption } from "./mockDataAutoSearch";
import {
  PaginatedYachtResponse,
  YachtsLocationsResponse,
} from "../types/YachtDetails";
import { getDefaultDateRange } from "./dateUtils";

// const baseUrl = "https://sailar-mada-56657a14304a.herokuapp.com";
const baseUrl = process.env.REACT_APP_API_URL || "https://sailarmada.com/api";

// Define the type of each boat type
export interface BoatTypeOption {
  id: number;
  label: string;
}

// Fetch boat types from the API
export const fetchBoatTypes = async (): Promise<BoatTypeOption[]> => {
  try {
    const response = await axios.get(`${baseUrl}/yachts/type/`);
    return response.data.map((item: string, index: number) => ({
      id: index + 1,
      label: item,
    }));
  } catch (error) {
    message.error("Failed to fetch boat types");
    // console.error("Failed to fetch boat types:", error);
    return [];
  }
};
// Fetch yacht model names from the API
export const fetchYachtModels = async (): Promise<string[]> => {
  try {
    const response = await axios.get(`${baseUrl}/yachts/models/`);
    return response.data.models || [];
  } catch (error) {
    return [];
  }
};

export const reserveThisBoat = async (reservationData: any) => {
  try {
    const response = await axios.post(
      `${baseUrl}/yachts/reserve/`,
      reservationData, // Pass the data object as the request body
    );
    return response.data;
  } catch (error) {
    message.error("Failed to reserve the boat");
    // console.error("Failed to reserve the boat:", error);
    return null;
  }
};

// Fetch destinations from the API
export const fetchdestinations = async () => {
  const normalizeDateToUTC = (date: Date) => {
    const offset = date.getTimezoneOffset() * 60000; // Offset in milliseconds
    return new Date(date.getTime() - offset).toISOString().split("Z")[0] + "Z";
  };

  // If dateRange is not selected, set default dates
  const [checkIn, checkOut] = getDefaultDateRange();

  const selectedCheckIn = normalizeDateToUTC(checkIn);
  const selectedCheckOut = normalizeDateToUTC(checkOut);
  try {
    const response = await axios.get(
      `${baseUrl}/yachts/availability-by-country/?countries=IT,GR,ES,FR,HR,TR&currency=EUR&date_from=${selectedCheckIn}&date_to=${selectedCheckOut}`,
    );
    return response;
  } catch (error) {
    message.error("Failed to fetch destinations");
    // console.error("Failed to fetch destinations:", error);
    return null;
  }
};

// Fetch yacht details
export const getYachtDetails = async (params: any) => {
  try {
    const response = await axios.get(
      `${baseUrl}/yachts/check-availability/?yachtId=${params.id}&year=${params.year}`,
    );
    return response;
  } catch (error: any) {
    return null;
  }
};

// Debounce helper function to limit API calls
export const debounce = (func: (...args: any[]) => void, delay: number) => {
  let timer: ReturnType<typeof setTimeout>;
  return (...args: any[]) => {
    clearTimeout(timer);
    timer = setTimeout(() => func(...args), delay);
  };
};

// Function to fetch location search results from the API
export const fetchLocationSearch = async (
  query: string,
): Promise<SearchOption[]> => {
  if (!query) return [];

  try {
    const response = await axios.get(
      `${baseUrl}/yachts/search/location/?query=${query}`,
    );

    return response.data.map((item: any) => ({
      id: item?.id ?? "",
      name: item?.type === 'country'
        ? (item?.country?.name || item?.country_name || "")
        : (item?.name === item?.country?.name ? "" : item?.name || ""),
      countryName: item?.type === 'country' ? "" : (item?.country?.name || ""),
      shortName: item?.country?.short_name || item?.short_name || "",
      imageUrl: item?.image_url || "",
      yachtCount: item?.yacht_count ?? 0,
      city: item?.city || "",
      type: item?.type || "",
      isSubItem: item?.is_sub_item ?? false,
      islandName: item?.island_name || "",
    }));
  } catch (error) {
    message.error("Failed to fetch locations");
    // console.error("Failed to fetch locations:", error);
    return [];
  }
};

export const searchYachts = async (
  dateFrom: string,
  dateTo: string,
  country: string,
  currency: string,
  yachtType: string,
  beds: number = 0,
  page: number = 1,
  city: string = "",
  id: string = "",
  type: string = "",
): Promise<{
  response: PaginatedYachtResponse;
  amenities: { label: string }[];
} | null> => {
  try {
    const searchParams = new URLSearchParams({
      date_from: `${dateFrom.split('T')[0]}T00:00:00`,
      date_to: `${dateTo.split('T')[0]}T00:00:00`,
      country,
      currency,
      yacht_type: yachtType,
      bed: beds.toString(),
      page: page.toString(),
      city,
      type,
    });
    const key =
      type === "base"
        ? "base_from_id"
        : type === "sailing_area"
          ? "sailing_area_id"
          : type === "island"
            ? "island_name"
            : "id";
    // For island type, the meaningful identifier is the island name (city), not the sailing area ID
    searchParams.set(key, type === "island" ? city : id);

    const response = await axios.get<PaginatedYachtResponse>(
      `${baseUrl}/yachts/search/?${searchParams}`,
    );
    const responseData = response.data; // Ensure correct type

    const seen = new Set<string>();
    const amenities: { label: string }[] = [];

    if (responseData && Array.isArray(responseData.results)) {
      for (const item of responseData.results) {
        if (item.yacht?.equipment_json) {
          for (const equipment of item.yacht.equipment_json) {
            const eqName: string = (equipment as any).name || "";
            if (eqName.trim() && !eqName.trim().toLowerCase().includes("unknown") && !seen.has(eqName)) {
              seen.add(eqName);
              amenities.push({ label: eqName });
            }
          }
        }
      }
    }

    return { response: responseData, amenities }; // Fix return type
  } catch (error) {
    console.error("Failed to fetch yacht search results:", error);
    message.error("Failed to fetch yacht search results");
    return null;
  }
};
export const searchYachtsLocation = async (
  dateFrom: string,
  dateTo: string,
  country: string,
  currency: string,
  yachtType: string,
  beds: number = 0,
  page: number = 1,
  city: string = "",
  id: string = "",
  type: string = "",
): Promise<{ response: YachtsLocationsResponse } | null> => {
  try {
    const searchParams = new URLSearchParams({
      date_from: `${dateFrom.split('T')[0]}T00:00:00`,
      date_to: `${dateTo.split('T')[0]}T00:00:00`,
      country,
      currency,
      yacht_type: yachtType,
      bed: beds.toString(),
      page: page.toString(),
      city,
      type,
    });
    const key =
      type === "base"
        ? "base_from_id"
        : type === "sailing_area"
          ? "sailing_area_id"
          : type === "island"
            ? "island_name"
            : "id";
    // For island type, the meaningful identifier is the island name (city), not the sailing area ID
    searchParams.set(key, type === "island" ? city : id);

    const response = await axios.get<YachtsLocationsResponse>(
      `${baseUrl}/yachts/map/?${searchParams}`,
    );
    const responseData = response.data; // Ensure correct type

    return { response: responseData }; // Fix return type
  } catch (error) {
    console.error("Failed to fetch yacht search results:", error);
    message.error("Failed to fetch yacht search results");
    return null;
  }
};

export const searchCounts = async (
  dateFrom: string,
  dateTo: string,
  country: string,
  currency: string,
  yachtType: string,
  beds: number = 0,
  page: number = 1, // New parameter for pagination
  city: string = "",
  id = "",
  type: string = "",
): Promise<PaginatedYachtResponse | null> => {
  try {
    // Construct the API URL with search parameters and pagination
    const searchParams = new URLSearchParams({
      date_from: `${dateFrom.split('T')[0]}T00:00:00`,
      date_to: `${dateTo.split('T')[0]}T00:00:00`,
      country,
      currency,
      yacht_type: yachtType,
      bed: beds.toString(),
      page: page.toString(), // Include the page parameter
      city,
      type,
    });
    const key =
      type === "base"
        ? "base_from_id"
        : type === "sailing_area"
          ? "sailing_area_id"
          : type === "island"
            ? "island_name"
            : "id";
    // For island type, the meaningful identifier is the island name (city), not the sailing area ID
    searchParams.set(key, type === "island" ? city : id);
    const response = await axios.get<PaginatedYachtResponse>(
      `${baseUrl}/yachts/count/?${searchParams}`,
    );
    return response.data; // Assuming response.data is the paginated result
  } catch (error) {
    message.error("Failed to fetch yacht search results");
    // console.error("Failed to fetch yacht search results:", error);
    return null;
  }
};

const fullMonthNames: Record<string, string> = {
  Jan: "January",
  Feb: "February",
  Mar: "March",
  Apr: "April",
  May: "May",
  Jun: "June",
  Jul: "July",
  Aug: "August",
  Sep: "September",
  Oct: "October",
  Nov: "November",
  Dec: "December",
};

const toFahrenheit = (celsius: number) => Math.round((celsius * 9) / 5 + 32);
interface LocationProps {
  country: string;
  latitude: number;
  longitude: number;
  name: string;
}
export const fetchWeather = async (location: LocationProps) => {
  try {
    const response = await axios.get(
      `${baseUrl}/yachts/weather/?latitude=${location.latitude}&longitude=${location.longitude}`,
    );
    const weather = response.data;

    if (!weather || !weather.months || weather.months.length === 0) {
      message.error("No weather data available");
      return null;
    }

    const monthsFull = weather.months.map(
      (month: string) => fullMonthNames[month] || month,
    );

    const minTemps = weather.minimum.map((temp: string) => parseInt(temp, 10));
    const maxTemps = weather.maximum.map((temp: string) => parseInt(temp, 10));
    const seaTemps = weather.sea.map((temp: number) => temp);

    const hottestIndex = maxTemps.indexOf(Math.max(...maxTemps));
    const coolestIndex = minTemps.indexOf(Math.min(...minTemps));
    const warmestSeaIndex = seaTemps.indexOf(Math.max(...seaTemps));

    const hottestMonth = monthsFull[hottestIndex] || "Unknown";
    const coolestMonth = monthsFull[coolestIndex] || "Unknown";
    const warmestSeaMonth = monthsFull[warmestSeaIndex] || "Unknown";

    const hottestTemp = maxTemps[hottestIndex] ?? "N/A";
    const coolestTemp = minTemps[coolestIndex] ?? "N/A";
    const warmestSeaTemp = seaTemps[warmestSeaIndex] ?? "N/A";

    const hottestTempF =
      typeof hottestTemp === "number" ? toFahrenheit(hottestTemp) : "N/A";
    const coolestTempF =
      typeof coolestTemp === "number" ? toFahrenheit(coolestTemp) : "N/A";
    const warmestSeaTempF =
      typeof warmestSeaTemp === "number" ? toFahrenheit(warmestSeaTemp) : "N/A";

    return {
      ...weather,
      hottestMonth,
      coolestMonth,
      warmestSeaMonth,
      hottestTemp,
      coolestTemp,
      warmestSeaTemp,
      hottestTempF,
      coolestTempF,
      warmestSeaTempF,
    };
  } catch (error) {
    message.error("Failed to fetch weather data");
    return null;
  }
};
export const filterYachts = async (
  filters: Record<string, any>,
): Promise<PaginatedYachtResponse | any> => {
  try {
    const response = await axios.post<PaginatedYachtResponse>(
      `${baseUrl}/yachts/filter/`,
      filters,
    );

    return response.data;
  } catch (error) {
    console.error("API Error:", error);
    message.error("Failed to fetch filtered yacht results");
    return null;
  }
};

export const searchAndFiltersYachts = async (
  payload: any,
  options?: { silent?: boolean },
): Promise<{
  response: any;
  amenities: { label: string }[];
} | null> => {
  try {
    const { page = 1, ...restPayload } = payload;
    const response = await axios.post(
      `${baseUrl}/yachts/search/?page=${page}`,
      restPayload,
    );
    const responseData = response.data;

    const seen = new Set<string>();
    const amenities: { label: string }[] = [];

    if (responseData?.search && Array.isArray(responseData?.search?.results)) {
      for (const item of responseData.search?.results) {
        if (item.yacht?.equipment_json) {
          for (const equipment of item.yacht.equipment_json) {
            const eqName: string = (equipment as any).name || "";
            if (eqName.trim() && !eqName.trim().toLowerCase().includes("unknown") && !seen.has(eqName)) {
              seen.add(eqName);
              amenities.push({ label: eqName });
            }
          }
        }
      }
    }

    return { response: responseData, amenities };
  } catch (error) {
    console.error("Failed to fetch yacht search results:", error);
    if (!options?.silent) {
      message.error("Failed to fetch yacht search results");
    }
    return null;
  }
};

export const getYachtInfo = async (
  payload: any,
): Promise<PaginatedYachtResponse | null> => {
  try {
    let { yachtId, ...restPayload } = payload;
    const response = await axios.post<PaginatedYachtResponse>(
      `${baseUrl}/yachts/detail/${yachtId}`,
      restPayload,
    );
    const responseData = response.data;
    return responseData;
  } catch (error) {
    message.error("Failed to fetch yacht details");
    return null;
  }
};

export const fetchFilterCounts = async (payload: any): Promise<any | null> => {
  try {
    const response = await axios.post(
      `${baseUrl}/yachts/filter-count/`,
      payload,
    );
    const responseData = response.data;

    return responseData;
  } catch (error) {
    message.error("Failed to fetch filter counts");
    return null;
  }
};
